/**
 * Where a ground move will land, for everyone to read (design docs/design/ui/2026-10-11-ground-aim/README.md, combat
 * spec §3.2–3.3): while a caster winds up a ground move (`cast.at`), its blast circle in the art's tint with a checker
 * closing in from the edge as the wind-up runs out; on the foe's side (anyone but the caster) a red 1-px outer edge and
 * 8 ticks lighting up round it (a countdown to the landing). Then, when it lands, the area it hit stays a moment (solid
 * checker, then thinning dots), so a ground move is always seen even with no wind-up. FX pixel rules: 1-px dots on the
 * scene grid, ramp tones, dither not alpha, 12 fps steps.
 */
import { getSkill } from '@nine/shared';
import {
  STAMP_LIFE,
  dotRing,
  checkerFill,
  foeSideOf,
  landStamps,
  landingMark,
  rampOfSkill,
  stepped,
  tone,
  type GroundPen,
} from '../groundMarkKit';
import { registerRenderPass } from './registry';

registerRenderPass({
  id: 'groundLanding',
  layer: 'ground',
  order: 221,
  draw({ g, world: w, iso, ox, oy }) {
    const pen: GroundPen = { g, iso, ox, oy };
    const now = w.time;
    let saved = false;
    for (const e of w.entities.values()) {
      const c = e.cast;
      if (!c?.at || e.dead) continue;
      const s = getSkill(c.skillId);
      if (!s.ground) continue;
      if (!saved) {
        g.save();
        g.globalAlpha = 1;
        saved = true;
      }
      const k = c.windup > 0 ? stepped(c.t) / c.windup : 1;
      landingMark(pen, c.at.x, c.at.y, s.aoe ?? 1, rampOfSkill(s.id), k, foeSideOf(w, e.id));
    }
    // The landed areas: solid for 2 frames, then the ring thins out (every 2nd dot, every 4th), then gone.
    for (let i = landStamps.length - 1; i >= 0; i--) {
      const m = landStamps[i]!;
      const age = now - m.t;
      if (age > STAMP_LIFE || age < -1) {
        landStamps.splice(i, 1);
        continue;
      }
      if (!saved) {
        g.save();
        g.globalAlpha = 1;
        saved = true;
      }
      const f = Math.floor(Math.max(0, age) * 12);
      const ramp = rampOfSkill(m.skillId);
      const foe = foeSideOf(w, m.src);
      const every = f < 2 ? 1 : f < 4 ? 2 : 4;
      if (f < 2) checkerFill(pen, m.x, m.y, m.r, tone(ramp, 4));
      dotRing(pen, m.x, m.y, m.r, tone(ramp, 6), every);
      if (foe) dotRing(pen, m.x, m.y, m.r + 0.11, tone('maroon', 4), every);
    }
    if (saved) g.restore();
  },
});
